/**
 * PlanOutput — renders the prioritize_plan step: the plan duration, a
 * month-by-month timeline (focus + actions), parallel tracks, and the weekly
 * schedule / budget metadata. Falls through to the narrative below.
 */

import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { ResumeStepData } from "../../../api/client";
import LessonMarkdown from "../../LessonMarkdown";
import { BulletSection, SectionLabel } from "./shared";

export default function PlanOutput({ step }: { step: ResumeStepData }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const o = step.output ?? {};
  const months: any[] = o.monthly_plan ?? [];
  const tracks: any[] = o.tracks ?? [];

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {typeof o.plan_duration_months === "number" && (
          <Chip
            label={t("PlanOutput.monthPlan", { months: o.plan_duration_months })}
            sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.primary.light, 0.1), color: theme.palette.primary.main }}
          />
        )}
        {o.budget_estimate && (
          <Chip
            label={t("PlanOutput.budgetLabel", { budget: o.budget_estimate })}
            variant="outlined"
            sx={{ fontWeight: 500, borderColor: alpha(theme.palette.primary.light, 0.4), color: theme.palette.primary.main }}
          />
        )}
      </Stack>

      {months.length > 0 && (
        <Box>
          <SectionLabel>{t("PlanOutput.monthByMonth")}</SectionLabel>
          <Stack
            spacing={0}
            mt={1}
            sx={{ borderLeft: `2px solid ${alpha(theme.palette.primary.light, 0.25)}`, pl: 2 }}
          >
            {months.map((m, i) => (
              <Box key={`month-${m?.month ?? i}`} sx={{ position: "relative", pb: i === months.length - 1 ? 0 : 2 }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: -25,
                    top: 4,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: theme.palette.primary.light,
                    border: `2px solid ${theme.palette.common.white}`,
                  }}
                />
                <Typography variant="subtitle2" fontWeight={500}>
                  {m?.month
                    ? t("PlanOutput.monthNumber", { month: m.month })
                    : t("PlanOutput.stepNumber", { step: i + 1 })}
                  {m?.focus ? t("PlanOutput.focusSuffix", { focus: m.focus }) : ""}
                </Typography>
                <BulletSection items={m?.actions} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {tracks.length > 0 && (
        <Box>
          <SectionLabel>{t("PlanOutput.tracks")}</SectionLabel>
          <Stack spacing={1.5} mt={1}>
            {tracks.map((trk, i) => (
              <Box
                key={`track-${trk?.name ?? i}`}
                sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.light, 0.04) }}
              >
                <Typography variant="subtitle2" fontWeight={500} mb={0.5}>
                  {trk?.name ?? t("PlanOutput.trackFallback", { index: i + 1 })}
                </Typography>
                <BulletSection items={trk?.items} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {o.weekly_schedule && (
        <Box>
          <SectionLabel>{t("PlanOutput.weeklySchedule")}</SectionLabel>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {o.weekly_schedule}
          </Typography>
        </Box>
      )}

      <LessonMarkdown>{step.narrative_markdown ?? ""}</LessonMarkdown>
    </Stack>
  );
}
